<template>
  <div class="editor-canvas-badges">
    <div
      v-for="(option, index) in badgesOptions"
      :key="index"
      class="editor-canvas-badges__wrap"
      :class="{ 'editor-canvas-badges__wrap--active': index === badgesOptionsIndex }"
      :style="optionWrapStyle"
    >
      <div
        ref="badgesOptions"
        class="editor-canvas-badges__option"
      >
        <NebulaBadge
          v-if="option.showPageBadge"
          size="s"
          :text="pageBadgeText"
        />
        <NebulaBadge
          v-if="option.showVideoBadge"
          size="s"
          icon="circle-play--filled"
          :text="option.videoBadgeText"
        />
        <NebulaBadge
          v-if="option.showAudioBadge"
          size="s"
          icon="circle-play--filled"
          :text="option.audioBadgeText"
        />
        <NebulaBadge
          v-if="option.showQuestionBadge"
          size="s"
          icon="circle-question--filled"
          :text="option.questionBadgeText"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { throttle } from 'lodash-es';
import { mapState } from 'pinia';
import { NebulaBadge } from '@discoveryedu/nebula-components';
import pageModuleTypes from '@/mixins/pageModuleTypes';
import {
  EDITOR_CANVAS_WIDTH,
  EDITOR_ZOOM_CONTENT_HEADER_HEIGHT,
} from '@/lib/constants';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'EditorCanvasBadges',
  components: {
    NebulaBadge,
  },
  mixins: [pageModuleTypes],
  props: {
    badges: {
      type: Object,
      required: true,
    },
    canvasScale: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      badgesOptionsIndex: 0,
    };
  },
  created() {
    this.setOptionIndexThrottled = throttle(this.setOptionIndex, 20);
  },
  mounted() {
    this.setOptionIndex();
  },
  watch: {
    canvasScale() {
      this.setOptionIndexThrottled();
    },
    badgesOptions() {
      this.setOptionIndexThrottled();
    },
  },
  methods: {
    setOptionIndex() {
      this.$nextTick(() => {
        const lastIndex = this.badgesOptions.length - 1;
        /*
          Pick the largest size badges option that fits within the
          canvas boundaries. If none fit, pick the last/smallest option.
        */
        for (let index = 0; index <= lastIndex; index += 1) {
          const optionEl = this.$refs?.badgesOptions?.[index];
          if (optionEl) {
            const badgesRect = optionEl.getBoundingClientRect();
            if (badgesRect.width <= this.scaledCanvasWidth || index === lastIndex) {
              this.badgesOptionsIndex = index;
              break;
            }
          }
        }
      });
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'currentPageIndex',
      'draftType',
      'pagesSorted',
    ]),
    optionWrapStyle() {
      return {
        width: `${this.scaledCanvasWidth}px`,
        height: `${EDITOR_ZOOM_CONTENT_HEADER_HEIGHT}px`,
      };
    },
    scaledCanvasWidth() {
      return this.canvasScale * EDITOR_CANVAS_WIDTH;
    },
    badgesOptions() {
      /*
        Render different badge size options for different canvas sizes.
      */
      const showPageBadge = this.badges.page;
      const showVideoBadge = this.badges.video && this.pageHasVideo(this.currentPage);
      const showAudioBadge = this.badges.audio && this.pageHasAudio(this.currentPage);
      const showQuestionBadge = this.badges.question && this.pageHasQuestion(this.currentPage);
      const baseOption = {
        showPageBadge,
        showVideoBadge,
        showAudioBadge,
        showQuestionBadge,
      };
      return [
        {
          ...baseOption,
          videoBadgeText: this.$t('Video'),
          audioBadgeText: this.$t('Audio'),
          questionBadgeText: this.$t('Questions'),
        },
        {
          ...baseOption,
          videoBadgeText: null,
          audioBadgeText: null,
          questionBadgeText: null,
          /*
            Don't show the audio badge if we're not showing badge text and
            both the audio and video badges are active since they have the
            same icon.
          */
          showAudioBadge: baseOption.showAudioBadge && !baseOption.showVideoBadge,
        },
      ];
    },
    pageBadgeText() {
      const args = {
        page: this.currentPageIndex + 1,
        count: this.pagesSorted.length,
      };
      return this.draftType !== 'board'
        ? this.$t('Slide %(page)d/%(count)d', args)
        : this.$t('Page %(page)d/%(count)d', args);
    },
  },
};
</script>

<style lang="stylus">
.editor-canvas-badges {
  position: relative;

  &__option {
    align-items: center;
    display: flex;
    gap: $nebula-space-1x;
    visibility: hidden;
    width: max-content;
  }

  &__wrap {
    left: 0;
    overflow: hidden;
    position: absolute;
    top: 0;

    &--active {
      overflow: visible;

      .editor-canvas-badges__option {
        visibility: visible;
      }
    }
  }

  .nebula-badge {
    --nebula-badge-border-radius: $nebula-space-4x;
    --nebula-badge-background-color: $nebula-color-platform-white;
    --nebula-badge-color: $nebula-color-platform-interface-1000;

    box-shadow: $nebula-shadow-200;
    white-space: nowrap;
  }
}
</style>
